// Subscription Manager - Freemium Feature Gating
// Handles subscription tiers and feature access control
// Last Updated: October 2025
import { getCheckoutAttribution } from './utils/attribution';
import { trackCheckoutStarted } from './utils/funnelAnalytics.js';
import { isOwnerAdviserUnlock } from './utils/adviserOwner';

export class SubscriptionManager {
    static TIERS = {
        FREE: 'free',
        ESSENTIAL: 'essential',  // Basic, Advanced, Accumulation
        PREMIUM: 'premium',      // All calculators including SMSF
        ADVISER: 'adviser',      // B2B practice desk licence (retirement only)
        PRO: 'pro'                // Alias for premium/essential (for compatibility)
    };

    static FEATURES = {
        // Free tier features (always accessible)
        TUTORIALS: 'tutorials', // Always free
        USER_MANUALS: 'user_manuals', // Always free
        DEMO_MODE: 'demo_mode',
        
        // Essential tier features ($14.99/month or $149/year)
        BASIC_CALCULATOR: 'basic_calculator',
        BASIC_WHATIFS: 'basic_whatifs',
        ADVANCED_CALCULATOR: 'advanced_calculator',
        ACCUMULATION_CALCULATOR: 'accumulation_calculator',
        MONTE_CARLO_SIMULATION: 'monte_carlo_simulation',
        HISTORICAL_BACKTESTING: 'historical_backtesting',
        ADVANCED_WITHDRAWAL_STRATEGIES: 'advanced_withdrawal_strategies',
        PDF_EXPORT: 'pdf_export',
        SAVE_RESTORE_SCENARIO: 'save_restore_scenario', // Basic & Accumulation: persist/restore inputs (Essential)
        
        // Premium tier features ($39.99/month or $399/year) 
        SMSF_SUITE: 'smsf_suite',
        SMSF_ALL_CALCULATORS: 'smsf_all_calculators',
        AI_RECOMMENDATIONS: 'ai_recommendations',
        PORTFOLIO_OPTIMIZATION: 'portfolio_optimization',
        ADVANCED_REPORTS: 'advanced_reports',
        PRIORITY_SUPPORT: 'priority_support',
        ADVISER_SUITE: 'adviser_suite',
    };

    static PRICING = {
        ESSENTIAL_MONTHLY: 14.99,
        ESSENTIAL_YEARLY: 149,
        PREMIUM_MONTHLY: 39.99,
        PREMIUM_YEARLY: 399,
        ADVISER_DESK_YEARLY: 1190,
        ADVISER_DESK_VOLUME_YEARLY: 1070,
        CURRENCY: 'AUD'
    };

    // Initialize subscription manager
    static initialize() {
        // LOCALHOST AUTO-BYPASS: Automatically grant premium / adviser access on localhost
        const isLocalhost = this.isLocalhostDevHost();
        const isAdviserApp = this.isAdviserAppContext();
        
        if (isLocalhost) {
            const simulateFree = localStorage.getItem('simulate_free_user') === '1';
            if (simulateFree) {
                // Force free tier so gate and run limit always apply
                this.downgradeToFree();
                console.log('🧪 Localhost: Simulating free user. Use "Dev mode: Adviser access" to switch back.');
            } else if (isAdviserApp) {
                let urlPractice = '';
                try {
                    urlPractice = (new URLSearchParams(window.location.search).get('practice') || '').trim();
                    if (urlPractice) {
                        localStorage.setItem('adviser_practice_name', urlPractice);
                    }
                } catch (_) {
                    /* ignore */
                }
                const practiceName = (localStorage.getItem('adviser_practice_name') || '').trim();
                this.grantLocalhostAdviserAccess({
                    practiceName,
                    forcePractice: Boolean(urlPractice),
                });
                console.log('🔓 Localhost Adviser: Auto-granted Adviser desk licence');
            } else {
                const stored = localStorage.getItem('subscription_tier');
                if (!stored || stored === 'free') {
                    const expiry = new Date();
                    expiry.setFullYear(expiry.getFullYear() + 1);
                    localStorage.setItem('subscription_tier', this.TIERS.PREMIUM);
                    localStorage.setItem('subscription_expiry', expiry.toISOString());
                    localStorage.setItem('subscription_id', 'localhost-dev');
                    localStorage.setItem('subscription_start', new Date().toISOString());
                    console.log('🔓 Localhost mode: Auto-granted premium subscription');
                }
            }
        }
        
        this.currentTier = this.loadSubscriptionStatus();
        this.trialEndDate = this.loadTrialEndDate();
        console.log('SubscriptionManager initialized:', this.currentTier);
    }

    static isLocalhostDevHost() {
        if (typeof window === 'undefined') return false;
        const h = window.location.hostname;
        return (h === 'localhost' || h === '127.0.0.1') &&
            !h.includes('netlify') &&
            !h.includes('supercalcpro');
    }

    /** True in production /adviser/ path, webpack-dev Adviser bundle, or port 3003. */
    static isAdviserAppContext() {
        if (typeof window === 'undefined') return false;
        if (window.__SCP_ADVISER_APP__ === true) return true;
        const path = window.location.pathname || '';
        if (/\/adviser(\/|$)/i.test(path)) return true;
        const port = String(window.location.port || '');
        return port === '3003';
    }

    /** Localhost-only full Adviser desk unlock (parity with Advanced/SMSF All Access). */
    static grantLocalhostAdviserAccess({ practiceName = '', forcePractice = false } = {}) {
        if (!this.isLocalhostDevHost()) return this.currentTier;
        const expiry = new Date();
        expiry.setFullYear(expiry.getFullYear() + 1);
        this.currentTier = this.TIERS.ADVISER;
        localStorage.setItem('subscription_tier', this.TIERS.ADVISER);
        localStorage.setItem('subscription_expiry', expiry.toISOString());
        localStorage.setItem('subscription_id', 'localhost-adviser-dev');
        localStorage.setItem('subscription_start', new Date().toISOString());
        localStorage.setItem('product_line', 'adviser');
        localStorage.setItem('seat_count', '3');
        localStorage.setItem('adviser_role', 'billing');
        if (!localStorage.getItem('subscription_email')) {
            localStorage.setItem('subscription_email', 'dev@localhost');
        }
        if (forcePractice || !localStorage.getItem('adviser_practice_name')) {
            localStorage.setItem('adviser_practice_name', practiceName);
        }
        localStorage.removeItem('simulate_free_user');
        return this.TIERS.ADVISER;
    }

    // B2B adviser desk checkout (annual seats, optional one-month firm trial)
    static async subscribeAdviser({
        email = '',
        quantity = 1,
        pilot = true,
        practiceName = '',
        checkoutContext = null,
    } = {}) {
        const qty = Math.max(1, Math.min(15, Number(quantity) || 1));
        if (qty > 15) {
            throw new Error('For 16+ desks, email support@supercalcpro.com.au for a firm quote.');
        }

        const isLocalhost = typeof window !== 'undefined' &&
            (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
            !window.location.hostname.includes('netlify') &&
            !window.location.hostname.includes('supercalcpro');
        if (isLocalhost) {
            this.grantLocalhostAdviserAccess({ practiceName: practiceName || '' });
            localStorage.setItem('seat_count', String(qty));
            window.location.reload();
            return;
        }

        const baseAttr = getCheckoutAttribution();
        const ctx = checkoutContext && typeof checkoutContext === 'object' ? checkoutContext : {};
        const attribution = {
            ...baseAttr,
            ...ctx,
            source: ctx.source || baseAttr.source || 'adviser_landing',
            landing_source: baseAttr.source || baseAttr.utm_source || 'adviser_landing',
            landing_path: baseAttr.landing_path || '/for-financial-advisers.html',
            latest_path: baseAttr.latest_path || window.location.pathname,
            surface: ctx.surface || 'adviser_checkout',
            cta_location: ctx.cta_location || ctx.surface || 'adviser_landing',
        };

        const response = await fetch('/.netlify/functions/create-checkout-session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                tier: 'adviser',
                billingCycle: 'annual',
                email,
                quantity: qty,
                pilot: Boolean(pilot),
                practiceName,
                attribution,
                success_url: `${window.location.origin}/adviser-payment-success.html`,
                cancel_url: `${window.location.origin}/for-financial-advisers.html#contact`,
            }),
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || data.error || 'Failed to create checkout session');
        }
        if (!data.url) {
            throw new Error('No checkout URL returned');
        }

        await trackCheckoutStarted({
            tier: 'adviser',
            billing: 'annual',
            source: attribution.source || 'adviser_landing',
            calculatorTier: 'adviser',
            surface: attribution.surface || 'adviser_checkout',
            pricingView: 'adviser_b2b',
        });

        window.location.href = data.url;
    }

    static applyVerifiedSubscription({
        tier,
        subscriptionId,
        currentPeriodEnd,
        productLine,
        seatCount,
        email,
        role,
    }) {
        const expiry = currentPeriodEnd
            ? new Date(currentPeriodEnd * 1000)
            : (() => {
                const d = new Date();
                d.setFullYear(d.getFullYear() + 1);
                return d;
            })();

        const resolvedTier = tier === 'adviser' ? this.TIERS.ADVISER
            : (tier === 'premium' ? this.TIERS.PREMIUM
            : (tier === 'essential' ? this.TIERS.ESSENTIAL : this.TIERS.PRO));

        localStorage.setItem('subscription_tier', resolvedTier);
        localStorage.setItem('subscription_expiry', expiry.toISOString());
        if (subscriptionId) localStorage.setItem('subscription_id', subscriptionId);
        localStorage.setItem('subscription_start', new Date().toISOString());
        if (productLine) localStorage.setItem('product_line', productLine);
        if (seatCount) localStorage.setItem('seat_count', String(seatCount));
        if (email) localStorage.setItem('subscription_email', email);
        if (role) localStorage.setItem('adviser_role', role);
        this.recordLicenceVerification();
        this.currentTier = resolvedTier;
        return resolvedTier;
    }

    static recordLicenceVerification() {
        try {
            localStorage.setItem('subscription_last_verified', String(Date.now()));
        } catch (_) {
            /* ignore */
        }
    }

    static getSubscriptionExpiryDate() {
        const raw = localStorage.getItem('subscription_expiry');
        if (!raw) return null;
        const d = new Date(raw);
        return Number.isNaN(d.getTime()) ? null : d;
    }

    static hasStoredStripeAdviserLicence() {
        const subId = localStorage.getItem('subscription_id') || '';
        if (!subId.startsWith('sub_')) return false;
        return this.isAdviserTier() ||
            localStorage.getItem('subscription_tier') === this.TIERS.ADVISER;
    }

    static isAdviserTier() {
        return this.currentTier === this.TIERS.ADVISER ||
            localStorage.getItem('product_line') === 'adviser';
    }

    static grantOwnerAdviserAccess(email = '') {
        const expiry = new Date();
        expiry.setFullYear(expiry.getFullYear() + 25);
        this.currentTier = this.TIERS.ADVISER;
        localStorage.setItem('subscription_tier', this.TIERS.ADVISER);
        localStorage.setItem('subscription_expiry', expiry.toISOString());
        localStorage.setItem('subscription_id', 'owner-adviser');
        localStorage.setItem('subscription_start', new Date().toISOString());
        localStorage.setItem('product_line', 'adviser');
        localStorage.setItem('seat_count', '1');
        localStorage.setItem('adviser_role', 'owner');
        if (email) localStorage.setItem('subscription_email', email);
        return this.TIERS.ADVISER;
    }

    static hasAdviserAccess() {
        if (this.isLocalhostDevHost() && this.isAdviserAppContext() && localStorage.getItem('simulate_free_user') !== '1') {
            if (this.currentTier !== this.TIERS.ADVISER) {
                this.grantLocalhostAdviserAccess();
            }
            return true;
        }
        if (isOwnerAdviserUnlock()) {
            if (this.currentTier !== this.TIERS.ADVISER) {
                this.grantOwnerAdviserAccess(localStorage.getItem('subscription_email') || '');
            }
            return true;
        }
        const paidTiers = [this.TIERS.ADVISER, this.TIERS.PREMIUM, this.TIERS.PRO];
        if (!paidTiers.includes(this.currentTier ?? this.TIERS.FREE)) return false;
        const expiryDate = localStorage.getItem('subscription_expiry');
        if (!expiryDate) return this.isAdviserTier();
        return new Date(expiryDate) > new Date();
    }

    // Upgrade to Essential or Premium via Stripe
    static async subscribe(tier = 'essential', billingCycle = 'monthly', email = '', couponCode = null, checkoutContext = null) {
        try {
            // Validate tier
            if (tier !== 'essential' && tier !== 'premium') {
                throw new Error('Invalid tier. Must be "essential" or "premium"');
            }

            // LOCALHOST BYPASS: Grant access directly without Stripe (ONLY on actual localhost, never in production)
            const isLocalhost = typeof window !== 'undefined' && 
                (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
                !window.location.hostname.includes('netlify') &&
                !window.location.hostname.includes('supercalcpro');
            if (isLocalhost) {
                console.log('Localhost mode: Granting subscription access without Stripe');
                // Set subscription tier - use PRO for compatibility with hasAccess checks
                // Both essential and premium should grant access to advanced calculator
                this.currentTier = this.TIERS.PRO;
                
                // Save to localStorage with 1 year expiry
                const expiry = new Date();
                expiry.setFullYear(expiry.getFullYear() + 1);
                localStorage.setItem('subscription_tier', this.TIERS.PRO);
                localStorage.setItem('subscription_expiry', expiry.toISOString());
                localStorage.setItem('subscription_id', 'localhost-test-' + Date.now());
                localStorage.setItem('subscription_start', new Date().toISOString());
                localStorage.setItem('subscription_billing', billingCycle);
                
                // Re-initialize to update currentTier
                this.initialize();
                
                // Refresh page to unlock inputs
                window.location.reload();
                return;
            }

            const knownEmail = email
                || this.getAdvancedFreeEmail()
                || localStorage.getItem('smsf_free_email')
                || '';
            const baseAttr = getCheckoutAttribution();
            const ctx = checkoutContext && typeof checkoutContext === 'object' ? checkoutContext : {};
            // Stripe is truth for landing + CTA: keep first-touch landing fields even when
            // checkoutContext.source is the paywall surface (post_result, pdf_export, …).
            const attribution = {
                ...baseAttr,
                ...ctx,
                landing_source: baseAttr.source || baseAttr.utm_source || '',
                article: baseAttr.article || ctx.article || '',
                cta_location: ctx.surface || ctx.cta_location || baseAttr.cta_location || '',
                landing_path: baseAttr.landing_path || '',
                latest_path: baseAttr.latest_path || '',
                surface: ctx.surface || baseAttr.surface || '',
            };

            const cancelUrl = `${window.location.origin}/app.html#advanced`;
            const response = await fetch('/.netlify/functions/create-checkout-session', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    tier, // 'essential' or 'premium'
                    billingCycle, // 'monthly' or 'annual'
                    email: knownEmail,
                    couponCode, // Optional coupon code
                    attribution,
                    // Return to Advanced with saved inputs after Stripe cancel (not homepage).
                    cancel_url: cancelUrl,
                })
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || data.error || 'Failed to create checkout session');
            }

            const { url } = data;
            if (!url) {
                throw new Error('No checkout URL returned');
            }

            await trackCheckoutStarted({
                tier,
                billing: billingCycle,
                source: attribution?.source || attribution?.utm_source || 'unknown',
                calculatorTier: attribution?.calculator_tier || 'unknown',
                surface: attribution?.surface || 'pricing_modal',
                pricingView: attribution?.pricing_view || 'simple'
            });

            // Redirect to Stripe Checkout (after GA4 beacon for checkout_started)
            window.location.href = url;
            
        } catch (error) {
            console.error('Subscription error:', error);
            throw error;
        }
    }

    // Legacy alias for backwards compatibility
    static async upgradeToPro(planType = 'monthly', email = '') {
        return this.subscribe('premium', planType, email);
    }

    // Load subscription status from localStorage
    static loadSubscriptionStatus() {
        const stored = localStorage.getItem('subscription_tier');
        const expiryDate = localStorage.getItem('subscription_expiry');
        
        // Check if subscription is still valid (for any paid tier)
        const paidTiers = [this.TIERS.ESSENTIAL, this.TIERS.PREMIUM, this.TIERS.PRO, this.TIERS.ADVISER];
        if (paidTiers.includes(stored) && expiryDate) {
            const expiry = new Date(expiryDate);
            if (expiry > new Date()) {
                return stored; // Return the actual tier (essential/premium/pro)
            } else {
                // Subscription expired
                this.downgradeToFree();
                return this.TIERS.FREE;
            }
        }
        
        return stored || this.TIERS.FREE;
    }

    // Load trial end date
    static loadTrialEndDate() {
        const stored = localStorage.getItem('trial_end_date');
        return stored ? new Date(stored) : null;
  }

  // Check if user has access to a feature
  static hasAccess(feature) {
        // Defensive: Basic calculator what-ifs are NEVER free (explicit deny for free tier)
        if (feature === this.FEATURES.BASIC_WHATIFS) {
            const tier = this.currentTier ?? this.TIERS.FREE;
            if (tier === this.TIERS.FREE) return false;
            return [this.TIERS.ESSENTIAL, this.TIERS.PREMIUM, this.TIERS.PRO].includes(tier);
        }

        // Features that are ALWAYS free for everyone
        const alwaysFreeFeatures = [
            this.FEATURES.BASIC_CALCULATOR,
            this.FEATURES.ACCUMULATION_CALCULATOR,
            this.FEATURES.TUTORIALS,
            this.FEATURES.USER_MANUALS,
            this.FEATURES.DEMO_MODE
        ];

        if (alwaysFreeFeatures.includes(feature)) {
            return true;
        }

        const smsfFeatures = [
            this.FEATURES.SMSF_SUITE,
            this.FEATURES.SMSF_ALL_CALCULATORS,
            this.FEATURES.AI_RECOMMENDATIONS,
            this.FEATURES.PORTFOLIO_OPTIMIZATION,
            this.FEATURES.ADVANCED_REPORTS,
        ];
        if (smsfFeatures.includes(feature)) {
            return [this.TIERS.PREMIUM, this.TIERS.PRO].includes(this.currentTier ?? this.TIERS.FREE);
        }

        // Check if user has any paid tier (essential, premium, adviser, or pro alias)
        const paidTiers = [this.TIERS.ESSENTIAL, this.TIERS.PREMIUM, this.TIERS.PRO, this.TIERS.ADVISER];
        return paidTiers.includes(this.currentTier ?? this.TIERS.FREE);
    }

    // Alias for hasAccess - defined inside class for robustness
    static hasFeatureAccess(feature) {
        return this.hasAccess(feature);
    }

    static isFree() {
        return this.currentTier === this.TIERS.FREE;
    }

    // Check if user can edit inputs (Pro only, or free tier calculators)
    static canEditInputs(calculatorType) {
        if (calculatorType === 'basic' || calculatorType === 'working-accumulation') {
            return true; // Free calculators - always editable
        }
        // Pro calculators - need any paid subscription
        const paidTiers = [this.TIERS.ESSENTIAL, this.TIERS.PREMIUM, this.TIERS.PRO, this.TIERS.ADVISER];
        return paidTiers.includes(this.currentTier);
    }

    // Check if in demo mode (viewing Pro feature without subscription)
    static isDemoMode(calculatorType) {
        if (calculatorType === 'basic' || calculatorType === 'working-accumulation') {
            return false; // Free calculators - not demo mode
        }
        return this.currentTier === this.TIERS.FREE; // Pro calculator + free user = demo mode
    }

    // Check if feature is locked for current user
    static isFeatureLocked(feature) {
        return !this.hasAccess(feature);
    }

    // Get current subscription tier
    static getCurrentTier() {
        return this.currentTier;
    }

    // Check if user is on free tier
    static isFreeTier() {
        return this.currentTier === this.TIERS.FREE;
    }

    // Check if user is on pro tier (any paid tier)
    static isProTier() {
        const paidTiers = [this.TIERS.ESSENTIAL, this.TIERS.PREMIUM, this.TIERS.PRO, this.TIERS.ADVISER];
        return paidTiers.includes(this.currentTier);
    }

    // Check if user has an active trial
    static hasActiveTrial() {
        const isTrial = localStorage.getItem('is_trial') === 'true';
        const expiryDate = localStorage.getItem('subscription_expiry');
        
        if (!isTrial || !expiryDate) {
            return false;
        }
        
        const expiry = new Date(expiryDate);
        const now = new Date();
        
        return now < expiry;
    }

    // Get trial info
    static getTrialInfo() {
        if (!this.hasActiveTrial()) {
            return null;
        }
        
        return {
            code: localStorage.getItem('trial_code'),
            email: localStorage.getItem('trial_email'),
            tier: localStorage.getItem('subscription_tier'),
            expiryDate: localStorage.getItem('subscription_expiry'),
            daysRemaining: Math.ceil((new Date(localStorage.getItem('subscription_expiry')) - new Date()) / (1000 * 60 * 60 * 24))
        };
    }

    // Check if trial has expired
    static hasTrialExpired() {
        const isTrial = localStorage.getItem('is_trial') === 'true';
        const expiryDate = localStorage.getItem('subscription_expiry');
        
        if (!isTrial || !expiryDate) {
            return false;
        }
        
        const expiry = new Date(expiryDate);
        const now = new Date();
        
        return now >= expiry;
    }

    // Upgrade to Pro tier
    static upgradeToProTier(subscriptionData) {
        this.currentTier = this.TIERS.PRO;
        
        // Calculate expiry based on subscription type
        const expiry = new Date();
        if (subscriptionData.interval === 'year') {
            expiry.setFullYear(expiry.getFullYear() + 1);
        } else {
            expiry.setMonth(expiry.getMonth() + 1);
        }
        
        localStorage.setItem('subscription_tier', this.TIERS.PRO);
        localStorage.setItem('subscription_expiry', expiry.toISOString());
        localStorage.setItem('subscription_id', subscriptionData.subscriptionId);
        localStorage.setItem('subscription_start', new Date().toISOString());
        
        // Track upgrade
      if (window.AnalyticsManager) {
            window.AnalyticsManager.trackEvent('subscription_upgrade', {
                tier: 'pro',
                interval: subscriptionData.interval,
                amount: subscriptionData.amount
            });
        }

        return true;
    }

    // Downgrade to free tier
    static downgradeToFree() {
        this.currentTier = this.TIERS.FREE;
        localStorage.setItem('subscription_tier', this.TIERS.FREE);
        localStorage.removeItem('subscription_expiry');
        localStorage.removeItem('subscription_id');
        
        // Track downgrade
      if (window.AnalyticsManager) {
            window.AnalyticsManager.trackEvent('subscription_downgrade', {
                tier: 'free'
            });
        }
    }

    // Start trial (7 days Pro features)
    static startTrial() {
        if (this.trialEndDate && this.trialEndDate > new Date()) {
            return false; // Trial already active
        }

        const trialEnd = new Date();
        trialEnd.setDate(trialEnd.getDate() + 7); // 7-day trial
        
        this.currentTier = this.TIERS.PRO;
        this.trialEndDate = trialEnd;
        
        localStorage.setItem('subscription_tier', this.TIERS.PRO);
        localStorage.setItem('trial_end_date', trialEnd.toISOString());
        localStorage.setItem('is_trial', 'true');
        
        // Track trial start
      if (window.AnalyticsManager) {
            window.AnalyticsManager.trackEvent('trial_started', {
                trial_days: 7
        });
      }
      
      return true;
    }

    // Check if on trial
    static isOnTrial() {
        const isTrial = localStorage.getItem('is_trial') === 'true';
        if (isTrial && this.trialEndDate) {
            return this.trialEndDate > new Date();
        }
        return false;
    }

    // Get days remaining in trial/subscription
    static getDaysRemaining() {
        const expiryDate = this.isOnTrial() 
            ? this.trialEndDate 
            : new Date(localStorage.getItem('subscription_expiry'));

        if (!expiryDate) return 0;

        const daysLeft = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));
        return Math.max(0, daysLeft);
    }

    // Get upgrade prompt message
    static getUpgradeMessage(feature) {
        const messages = {
            [this.FEATURES.ADVANCED_CALCULATOR]: 
                'Unlock Advanced Calculator with Monte Carlo simulations and historical backtesting',
            [this.FEATURES.BASIC_WHATIFS]: 
                'Unlock What-If Scenarios: live to age, extra income, fee changes, sequence-of-returns risk, and surviving spouse. Part of Advanced Retirement.',
            [this.FEATURES.MONTE_CARLO_SIMULATION]: 
                'Run 10,000 Monte Carlo simulations to test retirement success probability',
            [this.FEATURES.HISTORICAL_BACKTESTING]: 
                'Backtest your retirement plan against 98 years of actual market data (1928-2025)',
            [this.FEATURES.SMSF_SUITE]: 
                'Access SMSF calculator suite with 36 specialized planning tools',
            [this.FEATURES.AI_RECOMMENDATIONS]: 
                'Compare retirement strategies with detailed analysis',
            [this.FEATURES.ADVANCED_WITHDRAWAL_STRATEGIES]: 
                'Use 8 dynamic withdrawal strategies including Guyton-Klinger',
            [this.FEATURES.PORTFOLIO_OPTIMIZATION]: 
                'Optimize portfolio allocation with efficient frontier analysis',
            [this.FEATURES.PDF_EXPORT]: 
                'Download your results as a PDF report. Part of Advanced Retirement.',
            [this.FEATURES.SAVE_RESTORE_SCENARIO]: 
                'Save and restore your scenario across sessions. Part of Advanced Retirement.'
        };

        return messages[feature] || 'Upgrade to Advanced Retirement or All Access to unlock this feature';
    }

    // Get feature description for marketing
    static getFeatureDescription(feature) {
        const descriptions = {
            [this.FEATURES.ADVANCED_CALCULATOR]: 
                'Comprehensive retirement planning with Monte Carlo simulations, historical analysis, and 4 withdrawal strategies',
            [this.FEATURES.SMSF_SUITE]: 
                '36 specialized SMSF tools covering contributions, compliance, investments, pensions, and administration'
        };

        return descriptions[feature] || '';
    }

    // Get pricing for display
    static getPricing() {
        return {
            essential: {
                monthly: {
                    amount: this.PRICING.ESSENTIAL_MONTHLY,
                    interval: 'month',
                    displayPrice: `$${this.PRICING.ESSENTIAL_MONTHLY} ${this.PRICING.CURRENCY}/month`,
                    billedAs: 'Billed monthly'
                },
                annual: {
                    amount: this.PRICING.ESSENTIAL_YEARLY,
                    interval: 'year',
                    displayPrice: `$${this.PRICING.ESSENTIAL_YEARLY} ${this.PRICING.CURRENCY}/year`,
                    billedAs: 'Billed annually',
                    savings: `Save $${(this.PRICING.ESSENTIAL_MONTHLY * 12 - this.PRICING.ESSENTIAL_YEARLY).toFixed(2)} ${this.PRICING.CURRENCY}`
                }
            },
            premium: {
                monthly: {
                    amount: this.PRICING.PREMIUM_MONTHLY,
                    interval: 'month',
                    displayPrice: `$${this.PRICING.PREMIUM_MONTHLY} ${this.PRICING.CURRENCY}/month`,
                    billedAs: 'Billed monthly'
                },
                annual: {
                    amount: this.PRICING.PREMIUM_YEARLY,
                    interval: 'year',
                    displayPrice: `$${this.PRICING.PREMIUM_YEARLY} ${this.PRICING.CURRENCY}/year`,
                    billedAs: 'Billed annually',
                    savings: `Save $${(this.PRICING.PREMIUM_MONTHLY * 12 - this.PRICING.PREMIUM_YEARLY).toFixed(2)} ${this.PRICING.CURRENCY}`
                }
            }
        };
    }

    // Clear subscription data (for testing)
    static clearSubscription() {
        localStorage.removeItem('subscription_tier');
        localStorage.removeItem('subscription_expiry');
        localStorage.removeItem('subscription_id');
        localStorage.removeItem('subscription_start');
        localStorage.removeItem('trial_end_date');
        localStorage.removeItem('is_trial');
        this.currentTier = this.TIERS.FREE;
    }

    // ========================================
    // FREE RUN TRACKING (Advanced Calculator)
    // One clear free run, then email for 1 more (lead), then paywall
    // ========================================
    static MAX_ANONYMOUS_RUNS = 1;
    static MAX_ACCOUNT_RUNS = 1; // 1 more after email = 2 total

    static getAdvancedAnonRunCount() {
        return parseInt(localStorage.getItem('adv_anon_runs') || '0');
    }

    static getAdvancedAcctRunCount() {
        return parseInt(localStorage.getItem('adv_acct_runs') || '0');
    }

    static getAdvancedFreeEmail() {
        return localStorage.getItem('adv_free_email') || null;
    }

    static setAdvancedFreeEmail(email) {
        localStorage.setItem('adv_free_email', email.trim().toLowerCase());
    }

    // Returns { canRun, reason, runsLeft }
    // reason: 'subscribed' | 'anonymous' | 'free_account' | 'needs_email' | 'needs_upgrade'
    static canRunAdvancedFree() {
        if (this.hasAccess(this.FEATURES.ADVANCED_CALCULATOR)) {
            return { canRun: true, reason: 'subscribed', runsLeft: Infinity };
        }
        const email = this.getAdvancedFreeEmail();
        if (!email) {
            const used = this.getAdvancedAnonRunCount();
            const left = this.MAX_ANONYMOUS_RUNS - used;
            if (left > 0) return { canRun: true, reason: 'anonymous', runsLeft: left };
            return { canRun: false, reason: 'needs_email', runsLeft: 0 };
        }
        const used = this.getAdvancedAcctRunCount();
        const left = this.MAX_ACCOUNT_RUNS - used;
        if (left > 0) return { canRun: true, reason: 'free_account', runsLeft: left };
        return { canRun: false, reason: 'needs_upgrade', runsLeft: 0 };
    }

    static recordAdvancedRun() {
        if (this.hasAccess(this.FEATURES.ADVANCED_CALCULATOR)) return;
        const email = this.getAdvancedFreeEmail();
        if (email) {
            const count = this.getAdvancedAcctRunCount() + 1;
            localStorage.setItem('adv_acct_runs', count.toString());
        } else {
            const count = this.getAdvancedAnonRunCount() + 1;
            localStorage.setItem('adv_anon_runs', count.toString());
        }
    }

    static clearAdvancedFreeRuns() {
        localStorage.removeItem('adv_anon_runs');
        localStorage.removeItem('adv_acct_runs');
        localStorage.removeItem('adv_free_email');
    }

    // ========================================
    // FREE RUN TRACKING (SMSF Suite)
    // Higher-value audience: 1 anonymous run, then email for 1 more (2 total)
    // ========================================
    static MAX_SMSF_ANON_RUNS = 1;
    static MAX_SMSF_ACCT_RUNS = 1; // 1 more after email = 2 total

    static getSMSFAnonRunCount() {
        return parseInt(localStorage.getItem('smsf_anon_runs') || '0');
    }

    static getSMSFAcctRunCount() {
        return parseInt(localStorage.getItem('smsf_acct_runs') || '0');
    }

    static getSMSFFreeEmail() {
        return localStorage.getItem('smsf_free_email') || null;
    }

    static setSMSFFreeEmail(email) {
        localStorage.setItem('smsf_free_email', email.trim().toLowerCase());
    }

    // Returns { canRun, reason, runsLeft }
    // reason: 'subscribed' | 'anonymous' | 'free_account' | 'needs_email' | 'needs_upgrade'
    static canRunSMSFFree() {
        if (this.hasAccess(this.FEATURES.SMSF_SUITE)) {
            return { canRun: true, reason: 'subscribed', runsLeft: Infinity };
        }
        const email = this.getSMSFFreeEmail();
        if (!email) {
            const used = this.getSMSFAnonRunCount();
            const left = this.MAX_SMSF_ANON_RUNS - used;
            if (left > 0) return { canRun: true, reason: 'anonymous', runsLeft: left };
            return { canRun: false, reason: 'needs_email', runsLeft: 0 };
        }
        const used = this.getSMSFAcctRunCount();
        const left = this.MAX_SMSF_ACCT_RUNS - used;
        if (left > 0) return { canRun: true, reason: 'free_account', runsLeft: left };
        return { canRun: false, reason: 'needs_upgrade', runsLeft: 0 };
    }

    static recordSMSFRun() {
        if (this.hasAccess(this.FEATURES.SMSF_SUITE)) return;
        const email = this.getSMSFFreeEmail();
        if (email) {
            const count = this.getSMSFAcctRunCount() + 1;
            localStorage.setItem('smsf_acct_runs', count.toString());
        } else {
            const count = this.getSMSFAnonRunCount() + 1;
            localStorage.setItem('smsf_anon_runs', count.toString());
        }
    }

    static clearSMSFFreeRuns() {
        localStorage.removeItem('smsf_anon_runs');
        localStorage.removeItem('smsf_acct_runs');
        localStorage.removeItem('smsf_free_email');
    }

    // Get subscription info for display
    static getSubscriptionInfo() {
        return {
            tier: this.currentTier,
            isFree: this.isFreeTier(),
            isPro: this.isProTier(),
            isOnTrial: this.isOnTrial(),
            daysRemaining: this.getDaysRemaining(),
            expiryDate: localStorage.getItem('subscription_expiry'),
            subscriptionId: localStorage.getItem('subscription_id')
        };
    }
}

// Auto-initialize on import
if (typeof window !== 'undefined') {
    SubscriptionManager.initialize();
    // Expose globally for analytics tracking
    window.SubscriptionManager = SubscriptionManager;
    
    // Extreme robustness for cached components
    window.SubscriptionManager.hasAccess = SubscriptionManager.hasAccess;
    window.SubscriptionManager.hasFeatureAccess = SubscriptionManager.hasAccess;
    window.SubscriptionManager.isFree = SubscriptionManager.isFree;
    window.SubscriptionManager.isFreeTier = SubscriptionManager.isFree;
}

// Robustness: ensure methods are attached to the class object
SubscriptionManager.hasFeatureAccess = SubscriptionManager.hasAccess;
SubscriptionManager.isFree = SubscriptionManager.isFree;
SubscriptionManager.isFreeTier = SubscriptionManager.isFree;

export default SubscriptionManager;
